import React, { useState, useEffect, useMemo } from 'react';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import ReactPaginate from 'react-paginate';
import '../../assets/css/table-mobile-responsiveness.css';
import '../../assets/css/react-confirmation-style.css';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import supabase from '../../supabaseClient';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import debounce from 'lodash.debounce';

const RequestorApproval = () => {
  const user = useSelector((state) => state.auth.user);
  const approved_by_username = user.user_name;

  const [pendingUsers, setPendingUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Fetch pending requestor accounts
  const fetchPendingUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('is_approved', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingUsers(data || []);
    } catch (error) {
      console.error('Error fetching pending users:', error);
      toast.error('Failed to load pending requestor accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingUsers();

    // Subscribe to realtime changes
    const channel = supabase
      .channel('pending-users')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'users' },
        () => {
          fetchPendingUsers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Handle approve user
  const handleApprove = async (userToApprove) => {
    confirmAlert({
      title: 'Approve Account',
      message: `Are you sure you want to approve the account for "${userToApprove.user_name}"?`,
      buttons: [
        {
          label: 'Approve',
          onClick: async () => {
            try {
              const { error } = await supabase
                .from('users')
                .update({
                  is_approved: true,
                  approved_at: new Date().toISOString(),
                })
                .eq('user_id', userToApprove.user_id);

              if (error) throw error;

              // Create notification
              const { error: notificationError } = await supabase.from('notification').insert([
                {
                  user_id: null,
                  user_name: approved_by_username,
                  message: `Account "${userToApprove.user_name}" has been approved.`,
                  created_at: new Date().toISOString(),
                  type: 'user approval',
                },
              ]);

              if (notificationError) {
                console.warn('Approval notification insert failed:', notificationError);
              }

              toast.success(`Account "${userToApprove.user_name}" approved successfully!`);
              fetchPendingUsers();
            } catch (error) {
              console.error('Error approving user:', error);
              toast.error('Failed to approve account');
            }
          },
        },
        {
          label: 'Cancel',
          onClick: () => {},
        },
      ],
    });
  };

  // Handle reject user
  const handleReject = async (userToReject) => {
    confirmAlert({
      title: 'Reject Account',
      message: `Are you sure you want to reject and delete the account request for "${userToReject.user_name}"? This action cannot be undone.`,
      buttons: [
        {
          label: 'Reject & Delete',
          onClick: async () => {
            try {
              const { error } = await supabase
                .from('users')
                .delete()
                .eq('user_id', userToReject.user_id);

              if (error) throw error;

              // Create notification
              const { error: notificationError } = await supabase.from('notification').insert([
                {
                  user_id: null,
                  user_name: approved_by_username,
                  message: `Account request "${userToReject.user_name}" has been rejected and deleted.`,
                  created_at: new Date().toISOString(),
                  type: 'user rejection',
                },
              ]);

              if (notificationError) {
                console.warn('Rejection notification insert failed:', notificationError);
              }

              toast.success(`Account request "${userToReject.user_name}" rejected and deleted.`);
              fetchPendingUsers();
            } catch (error) {
              console.error('Error rejecting user:', error);
              toast.error('Failed to reject account');
            }
          },
        },
        {
          label: 'Cancel',
          onClick: () => {},
        },
      ],
    });
  };

  // View user details
  const openDetailModal = (user) => {
    setSelectedUser(user);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedUser(null);
  };

  // Search functionality with debounce
  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        setSearchTerm(term);
        setCurrentPage(0);
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  // Filter data based on search term
  const filteredData = pendingUsers.filter((row) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (row.user_name?.toLowerCase() || '').includes(searchLower) ||
      (row.first_name?.toLowerCase() || '').includes(searchLower) ||
      (row.last_name?.toLowerCase() || '').includes(searchLower) ||
      (row.department?.toLowerCase() || '').includes(searchLower) ||
      (row.role?.toLowerCase() || '').includes(searchLower)
    );
  });

  // Pagination
  const offset = currentPage * rowsPerPage;
  const currentData = filteredData.slice(offset, offset + rowsPerPage);
  const pageCount = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6 font-poppins">
      {/* Header Section */}
      <div className="relative border-b border-blue/10 pb-6 mb-6">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-10 w-32 h-32 bg-blue/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-10 w-40 h-40 bg-yellow/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-blue tracking-tight">
                Requestor Account Approvals
              </h1>
              <p className="text-grey mt-1">
                Review and approve pending requestor account registrations
              </p>
            </div>

            {/* Stats Badge */}
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-orange-100 text-orange-700 rounded-xl font-semibold">
                <i className="fas fa-clock mr-2"></i>
                {filteredData.length} Pending Approval{filteredData.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-6 max-w-md">
            <div className="relative">
              <input
                type="search"
                placeholder="Search by username, name, department, role..."
                onChange={handleSearchChange}
                className="w-full px-4 py-3 pl-12 border-2 border-gray-200 rounded-xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300"
              />
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i className="fas fa-search text-grey"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <div className="w-12 h-12 border-4 border-blue/20 rounded-full animate-spin border-t-blue mx-auto"></div>
            <p className="text-grey">Loading pending accounts...</p>
          </div>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="flex items-center justify-center min-h-[400px] bg-gray-50 rounded-2xl">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <i className="fas fa-check-circle text-green-500 text-3xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-grey">All Caught Up!</h3>
              <p className="text-grey/70">No pending requestor accounts to approve.</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white shadow-sm">
            <Table className="min-w-full table-auto border-collapse">
              <Thead>
                <Tr className="bg-blue/5">
                  <Th className="py-4 px-4 text-left text-blue font-semibold border-b border-blue/10">
                    Username
                  </Th>
                  <Th className="py-4 px-4 text-left text-blue font-semibold border-b border-blue/10">
                    Name
                  </Th>
                  <Th className="py-4 px-4 text-left text-blue font-semibold border-b border-blue/10">
                    Department
                  </Th>
                  <Th className="py-4 px-4 text-left text-blue font-semibold border-b border-blue/10">
                    Role
                  </Th>
                  <Th className="py-4 px-4 text-left text-blue font-semibold border-b border-blue/10">
                    Requested On
                  </Th>
                  <Th className="py-4 px-4 text-center text-blue font-semibold border-b border-blue/10">
                    Actions
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {currentData.map((row, index) => (
                  <Tr
                    key={row.user_id}
                    className={`${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    } hover:bg-blue/5 transition-colors`}
                  >
                    <Td className="py-3 px-4 border-b border-gray-100">
                      <span className="font-medium text-blue">{row.user_name}</span>
                    </Td>
                    <Td className="py-3 px-4 border-b border-gray-100">
                      {row.first_name && row.last_name
                        ? `${row.first_name} ${row.last_name}`
                        : <span className="text-grey/50 italic">Not provided</span>}
                    </Td>
                    <Td className="py-3 px-4 border-b border-gray-100">
                      <span className="px-3 py-1 bg-gray-100 text-grey rounded-full text-sm">
                        {row.department}
                      </span>
                    </Td>
                    <Td className="py-3 px-4 border-b border-gray-100">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        row.role === 'Requestor'
                          ? 'bg-purple-100 text-purple-700'
                          : row.role === 'Corporate'
                          ? 'bg-blue-100 text-blue-700'
                          : row.role === 'Employee'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {row.role}
                      </span>
                    </Td>
                    <Td className="py-3 px-4 border-b border-gray-100 text-grey text-sm">
                      {formatDate(row.created_at)}
                    </Td>
                    <Td className="py-3 px-4 border-b border-gray-100">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openDetailModal(row)}
                          className="p-2 text-blue hover:bg-blue/10 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button
                          onClick={() => handleApprove(row)}
                          className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                          title="Approve"
                        >
                          <i className="fas fa-check"></i>
                        </button>
                        <button
                          onClick={() => handleReject(row)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Reject"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-grey">Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(0);
                  }}
                  className="border border-gray-200 rounded-lg px-3 py-1 text-sm focus:border-blue focus:ring-2 focus:ring-blue/10"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <ReactPaginate
                previousLabel={<i className="fas fa-chevron-left"></i>}
                nextLabel={<i className="fas fa-chevron-right"></i>}
                pageCount={pageCount}
                onPageChange={handlePageChange}
                containerClassName="flex items-center gap-1"
                pageClassName="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-blue/10 transition-colors"
                pageLinkClassName="w-full h-full flex items-center justify-center text-sm"
                previousClassName="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-blue/10 transition-colors"
                nextClassName="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-blue/10 transition-colors"
                activeClassName="bg-blue text-white hover:bg-blue"
                disabledClassName="opacity-50 cursor-not-allowed"
                forcePage={currentPage}
              />
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {isDetailModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeDetailModal}
          ></div>

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Modal Header */}
            <div className="bg-blue px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <i className="fas fa-user text-white"></i>
                  </div>
                  <h2 className="text-white font-bold text-lg">Account Details</h2>
                </div>
                <button
                  onClick={closeDetailModal}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <i className="fas fa-times text-white"></i>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-grey/70 uppercase tracking-wide">Username</label>
                  <p className="font-semibold text-blue">{selectedUser.user_name}</p>
                </div>
                <div>
                  <label className="text-xs text-grey/70 uppercase tracking-wide">Role</label>
                  <p className="font-semibold">{selectedUser.role}</p>
                </div>
                <div>
                  <label className="text-xs text-grey/70 uppercase tracking-wide">First Name</label>
                  <p className="font-medium">{selectedUser.first_name || 'Not provided'}</p>
                </div>
                <div>
                  <label className="text-xs text-grey/70 uppercase tracking-wide">Last Name</label>
                  <p className="font-medium">{selectedUser.last_name || 'Not provided'}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-grey/70 uppercase tracking-wide">Department</label>
                  <p className="font-medium">{selectedUser.department}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-grey/70 uppercase tracking-wide">Requested On</label>
                  <p className="font-medium">{formatDate(selectedUser.created_at)}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    closeDetailModal();
                    handleReject(selectedUser);
                  }}
                  className="flex-1 py-3 px-4 rounded-xl border-2 border-red-200 text-red-600 font-semibold hover:bg-red-50 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <i className="fas fa-times"></i>
                  Reject
                </button>
                <button
                  onClick={() => {
                    closeDetailModal();
                    handleApprove(selectedUser);
                  }}
                  className="flex-1 py-3 px-4 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <i className="fas fa-check"></i>
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestorApproval;
